import React, { useState, useEffect, useContext, useMemo } from "react";
import { Table, Button, Select } from "antd";
import styled from "styled-components";
import { FilePdfOutlined } from "@ant-design/icons";
import { ToastContextDispatch, UserInfoContext } from "Context/CommonContext";
import {
  BASE_URL,
  GET_STUDENT_REPORT,
} from "utilFunctions/Constants/urlConstants";
import getRequestInterface from "utilFunctions/Axios/getRequestInterface";
import { getAPIRequest } from "utilFunctions/Axios/AxiosRequestConfiguration";
import { useParams } from "react-router-dom";
import { capitalizeFirstLetter } from "Pages/StudentPortfolio/StudentCommentSection/StudentCommentListingBox";

const TableContainer = styled.div`
  margin-top: 16px;
  .ant-table-wrapper {
    border: 1px solid #f0f0f0;
    border-radius: 8px;
  }
`;

const HeaderDiv = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-bottom: 16px;
`;

const IconButton = styled(Button)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
`;

const formatCoverage = (start: string, end: string) => {
  if (!start || !end) return "-";
  const startDate = new Date(start);
  const endDate = new Date(end);
  const startDay = startDate.getDate();
  const startMonth = startDate.toLocaleDateString("en-GB", { month: "short" });
  const endDay = endDate.getDate();
  const endMonth = endDate.toLocaleDateString("en-GB", { month: "short" });
  const endYear = endDate.getFullYear();
  return `${startDay} ${startMonth} - ${endDay} ${endMonth} ${endYear}`;
};

const RepordCard = () => {
  const { id } = useParams<{ id: string }>();
  const [stream, setStream] = useState<string | undefined>(undefined);
  const [type, setType] = useState<string | undefined>(undefined);
  const [allData, setAllData] = useState<any[]>([]);
  const [token, setToken] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const userInfo = useContext<any>(UserInfoContext);
  const ToastDispatch = useContext<any>(ToastContextDispatch);
  const streams = userInfo?.userStreams;

  const filteredData = useMemo(() => {
    const stream_label =
      streams?.find((s: any) => s.value === stream)?.label || "";
    return allData.filter((item) => {
      const matchStream = !stream || item.stream === stream_label;
      const matchType =
        !type || item.type?.toLowerCase() === type.toLowerCase();
      return matchStream && matchType;
    });
  }, [allData, stream, type]);

  useEffect(() => {
    if (!id) return;
    fetchData();
  }, [id]);

  const fetchData = async () => {
    setLoading(true);
    await getAPIRequest({
      url: BASE_URL + GET_STUDENT_REPORT + id,
      success: (response: any) => {
        const data = response?.data?.data || {};
        const { report_card, token } = data || {};
        setToken(token);
        const formattedData: any = report_card.map(
          (item: any, index: number) => ({
            key: item.id || index.toString(),
            monthPeriod: item.title || "-",
            stream: item.stream || "-",
            coverage: formatCoverage(
              item.coverage_period_start,
              item.coverage_period_end
            ),
            status: item.url ? "Generated" : "Pending",
            url: item.url,
            type: item.type,
          })
        );
        setAllData(formattedData);
        setLoading(false);
      },
      failure: (err: any) => {
        ToastDispatch({
          isOpen: true,
          type: "Error",
          message: err?.message,
        });
        setLoading(false);
      },
    } as getRequestInterface);
  };

  const columns = [
    {
      title: "Report Month/Period",
      dataIndex: "monthPeriod",
      key: "monthPeriod",
      render: (text: string) => <span style={{ fontWeight: 500 }}>{text}</span>,
    },
    {
      title: "Stream",
      dataIndex: "stream",
      key: "stream",
      render: (text: string) => <span style={{ fontWeight: 500 }}>{text}</span>,
    },
    {
      title: "Coverage",
      dataIndex: "coverage",
      key: "coverage",
      render: (text: string) => <span style={{ fontWeight: 500 }}>{text}</span>,
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      render: (text: string) => (
        <span style={{ fontWeight: 500 }}>
          {text && capitalizeFirstLetter(text)}
        </span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: any, record: any) => {
        const handleOpenUrl = () => {
          if (record.url) {
            const newUrl = record.url.split("lms/")?.[0];
            const playerUrl = newUrl.replace(/\/$/, "");
            const popup: any = window.open(record.url, "_blank");
            console.log("report url", playerUrl);
            window.addEventListener(
              "message",
              (event) => {
                console.log("HANDSHAKE INITIALISE");
                if (event.origin !== playerUrl) return;
                if (event.data === "REPORT_CARD_READY") {
                  // Send the Player Login token via secure memory channel
                  console.log("PLAYER_READY_EVENT RECEIVED");
                  popup.postMessage(
                    { type: "SEND_REPORT_TOKEN", token: token },
                    event.origin
                  );
                }
              },
              { once: false }
            );
          }
        };

        return (
          <div style={{ display: "flex", gap: "8px" }}>
            <IconButton onClick={handleOpenUrl} disabled={!record.url}>
              <FilePdfOutlined style={{ color: "#00b0f5" }} />
            </IconButton>
          </div>
        );
      },
    },
  ];

  return (
    <div>
      <HeaderDiv>
        <Select
          placeholder="Select Stream"
          value={stream}
          onChange={(value) => setStream(value)}
          style={{ width: 140 }}
          options={streams}
          allowClear
        />
        <Select
          placeholder="Select Type"
          value={type}
          onChange={(value) => setType(value)}
          style={{ width: 120 }}
          options={[
            { value: "monthly", label: "Monthly" },
            { value: "quarterly", label: "Quarterly" },
          ]}
          allowClear
        />
      </HeaderDiv>
      <TableContainer>
        <Table
          columns={columns}
          dataSource={filteredData}
          pagination={false}
          size="small"
          loading={loading}
          scroll={{ x: "max-content" }}
        />
      </TableContainer>
    </div>
  );
};

export default RepordCard;
